import styles from "./team-preferences.module.css";
import { useContext, useEffect, useState } from "react";
import { MembershipContext } from "hoc/membership-data";
import instance from "services/api";
import { MultiSelect } from "primereact/multiselect";
import { Dropdown } from "primereact/dropdown";
import Spinner from "components/functional/spinner";
import { days, hours } from "constants/preferences.constants";
import { ToastService, ToastComponent } from "services/toast.service";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router";
import { MoveLeft } from "lucide-react";

const TeamPreferences = () => {
  const [preferences, setPreferences] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [startTimeError, setStartTimeError] = useState(""); // Error for startTime
  const [endTimeError, setEndTimeError] = useState(""); // Error for endTime
  const [isRunning, setIsRunning] = useState();
  const [sportPlaces, setSportPlaces] = useState([]);

  const { t } = useTranslation();

  const membershipData = useContext(MembershipContext);
  const { teams } = membershipData;
  const { team_id, name } = teams[0];

  const translatedDays = days.map((day) => ({
    ...day,
    name: t(`team-preferences.form.day-names.${day.code.toUpperCase()}`),
  }));

  const handlePreferenceChange = (field, value) => {
    if (field === "endTime" && preferences.startTime) {
      const startTimeIndex = hours.findIndex(
        (h) => h.code === preferences.startTime.code
      );
      const endTimeIndex = hours.findIndex((h) => h.code === value.code);
      if (endTimeIndex <= startTimeIndex) {
        setEndTimeError(t("team-preferences.form.end.error"));
      } else {
        setEndTimeError("");
        setStartTimeError("");
      }
    }

    if (field === "startTime" && preferences.endTime) {
      const startTimeIndex = hours.findIndex((h) => h.code === value.code);
      const endTimeIndex = hours.findIndex(
        (h) => h.code === preferences.endTime.code
      );
      if (startTimeIndex >= endTimeIndex) {
        setStartTimeError(t("team-preferences.form.start.error"));
      } else {
        setStartTimeError("");
        setEndTimeError("");
      }
    }

    setPreferences((prev) => ({ ...prev, [field]: value }));
  };

  const savePreferences = async () => {
    try {
      setIsRunning(true);
      const formattedPreferences = preferences.selectedDays.flatMap((day) =>
        preferences.selectedCenters.map((center) => ({
          day_of_week: day.code,
          start_time: preferences.startTime.code,
          end_time: preferences.endTime.code,
          sport_place_code: center.code,
        }))
      );

      await instance.post(`/team-preferences/${team_id}`, {
        preferences: formattedPreferences,
      });
      ToastService.showSuccessToast(t("team-preferences.form.save.success"));
    } catch (error) {
      console.log(error);
      ToastService.showErrorToast(t("team-preferences.form.save.error"));
    } finally {
      setIsRunning(false);
    }
  };

  useEffect(() => {
    const fetchPreferences = async (centers) => {
      try {
        const response = await instance.get(`/team-preferences/${team_id}`);

        const transformedPreferences = {
          selectedDays: [
            ...new Set(
              response.data.map((pref) =>
                translatedDays.find(
                  (d) => d.code === pref.day_of_week.toUpperCase()
                )
              )
            ),
          ],
          selectedCenters: [
            ...new Set(
              response.data.map((pref) =>
                centers.find(
                  (c) => c.code === pref.sport_place_code.toUpperCase()
                )
              )
            ),
          ],
          startTime: hours.find((h) => h.code === response.data[0].start_time),
          endTime: hours.find((h) => h.code === response.data[0].end_time),
        };

        setPreferences(transformedPreferences);
      } catch (error) {
        console.log(error);
      }
    };

    const fetchData = async () => {
      try {
        // First fetch sport places
        const sportPlacesResponse = await instance.get(`/sport-places`);
        const transformedSportPlaces = sportPlacesResponse.data.map(
          (place) => ({
            name: place.name,
            code: place.code,
          })
        );
        setSportPlaces(transformedSportPlaces);

        // Then fetch preferences using the transformed sport places
        await fetchPreferences(transformedSportPlaces);
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  if (isLoading) return <Spinner />;

  return (
    <div className="dashboard-container">
      <ToastComponent />
      <div className={`${styles["title-wrapper"]} mb-32`}>
        <NavLink className="btn btn--transparent" to={-1}>
          <MoveLeft size={20} />
        </NavLink>
        <h1 className="title-1">{t("team-preferences.title")}</h1>
      </div>
      <section className={styles.wrapper}>
        <div className={`${styles.container} mb-24`}>
          <header className={`${styles.header} mb-16`}>
            <div className={styles.icon}>
              <p className="title-3-variant-low">{name[0]}</p>
            </div>
            <h2 className={`${styles.title} title-3-variant-low`}>{name}</h2>
          </header>
          <form className={styles.form}>
            <div className="form-group">
              <label htmlFor="days">
                {t("team-preferences.form.days.title")}
              </label>
              <MultiSelect
                value={preferences.selectedDays}
                onChange={(e) =>
                  handlePreferenceChange("selectedDays", e.value)
                }
                options={translatedDays}
                filter
                optionLabel="name"
                placeholder={t("team-preferences.form.days.placeholder")}
                data-testid="data-test-days"
              />
            </div>
            <div className="form-group">
              <label htmlFor="centers">
                {t("team-preferences.form.centers.title")}
              </label>
              <MultiSelect
                value={preferences.selectedCenters}
                onChange={(e) =>
                  handlePreferenceChange("selectedCenters", e.value)
                }
                options={sportPlaces}
                filter
                optionLabel="name"
                placeholder={t("team-preferences.form.centers.placeholder")}
                emptyFilterMessage={t("team-preferences.form.centers.empty")}
                data-testid="data-test-centers"
              />
            </div>
            <div className="form-group">
              <label htmlFor="start">
                {t("team-preferences.form.start.title")}
              </label>
              <Dropdown
                value={preferences.startTime}
                onChange={(e) => handlePreferenceChange("startTime", e.value)}
                options={hours}
                optionLabel="name"
                placeholder={t("team-preferences.form.start.placeholder")}
                className={startTimeError ? styles.error : ""}
                data-testid="data-test-start-time"
              />
              {startTimeError && (
                <p className="error-message">{startTimeError}</p>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="end">
                {t("team-preferences.form.end.title")}
              </label>
              <Dropdown
                value={preferences.endTime}
                onChange={(e) => handlePreferenceChange("endTime", e.value)}
                options={hours}
                optionLabel="name"
                placeholder={t("team-preferences.form.end.placeholder")}
                className={endTimeError ? styles.error : ""}
                data-testid="data-test-end-time"
              />
              {endTimeError && <p className="error-message">{endTimeError}</p>}
            </div>
          </form>
        </div>
        <div className={styles.actions}>
          <button
            type="submit"
            disabled={isRunning || startTimeError || endTimeError}
            className="btn btn--primary"
            onClick={savePreferences}
            data-testid="data-test-save-preferences"
          >
            {t("actions.save")}
            {isRunning && <Spinner className="action-loader"></Spinner>}
          </button>
        </div>
      </section>
    </div>
  );
};

export default TeamPreferences;
